import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import OutsideClickHandler from "react-outside-click-handler";
import styles from "./Dropdown.module.sass";
import Tooltip from "../Tooltip";

const Dropdown = ({
  className,
  classDropdownHead,
  classDropdownLabel,
  value,
  setValue,
  options,
  label,
  tooltip,
  small,
  upBody,
  searchable = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const optionRefs = useRef([]);

  const handleClick = (value) => {
    setValue(value);
    setVisible(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
  };

  const filteredOptions = searchable && searchTerm
    ? options?.filter((option) => {
        const displayName = option?.title || option?.name || "";
        return displayName.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : options;

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (visible && searchable && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
    if (!visible) {
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  }, [visible, searchable]);

  // Auto-highlight first option when searching
  useEffect(() => {
    if (searchTerm && filteredOptions && filteredOptions.length > 0) {
      setHighlightedIndex(0);
    } else if (!searchTerm) {
      setHighlightedIndex(-1);
    }
  }, [searchTerm, filteredOptions]);

  // Scroll to highlighted option
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e) => {
    if (!visible) return;

    const totalOptions = filteredOptions?.length || 0;
    const showSelect = !searchTerm;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < totalOptions - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        const minIndex = showSelect ? -1 : 0;
        setHighlightedIndex((prev) => (prev > minIndex ? prev - 1 : minIndex));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredOptions && filteredOptions.length > 0) {
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            handleClick(filteredOptions[highlightedIndex]);
          } else if (highlightedIndex === -1 && showSelect) {
            // Clear selection
            setValue("");
            setSearchTerm("");
            setVisible(false);
          } else if (filteredOptions.length === 1 || (!showSelect && highlightedIndex === -1)) {
            // Auto-select if only one option or if search is active and nothing highlighted
            handleClick(filteredOptions[0]);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        setVisible(false);
        break;
      default:
        break;
    }
  };

  console.log(options);
  return (
    <OutsideClickHandler onOutsideClick={() => setVisible(false)}>
      {label && (
        <div className={cn(styles.label, classDropdownLabel)}>
          {label}{" "}
          {tooltip && (
            <Tooltip
              className={styles.tooltip}
              title={tooltip}
              icon="info"
              place="right"
            />
          )}
        </div>
      )}
      <div
        style={{
          backgroundColor: "#F4F4F4",
          borderRadius: 10,
        }}
        className={cn(
          styles.dropdown,
          className,
          { [styles.small]: small },
          {
            [styles.active]: visible,
          }
        )}
        onKeyDown={handleKeyDown}
      >
        <div
          className={cn(styles.head, classDropdownHead)}
          onClick={() => setVisible(!visible)}
        >
          <div className={styles.selection}>
            {value?.title ? value?.title : value}
          </div>
        </div>
        <div className={cn(styles.body, { [styles.bodyUp]: upBody })}>
          {searchable && visible && (
            <div style={{ padding: "8px", position: "sticky", top: 0, background: "inherit", zIndex: 1 }}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  outline: "none",
                }}
              />
            </div>
          )}
          <div style={{ maxHeight: searchable ? "250px" : "300px", overflowY: "auto" }}>
            {!searchTerm && (
              <div
                ref={(el) => (optionRefs.current[-1] = el)}
                className={cn(styles.option, {
                  [styles.highlighted]: highlightedIndex === -1,
                })}
                onClick={() => {
                  setValue("");
                  setSearchTerm("");
                  setHighlightedIndex(-1);
                }}
                onMouseEnter={() => setHighlightedIndex(-1)}
                key={0}
              >
                Select
              </div>
            )}
            {filteredOptions?.map((x, index) => (
              <div
                ref={(el) => (optionRefs.current[index] = el)}
                className={cn(styles.option, {
                  [styles.selectioned]: x?.title === value || x?.name === value,
                  [styles.highlighted]: highlightedIndex === index,
                })}
                onClick={() => handleClick(x)}
                onMouseEnter={() => setHighlightedIndex(index)}
                key={index}
              >
                {x?.title ? x?.title : x?.name ? x?.name :''}
              </div>
            ))}
          </div>
        </div>
      </div>
    </OutsideClickHandler>
  );
};

export default Dropdown;
